import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Link,
  LinkProps,
} from "@chakra-ui/react";
import { RemixiconComponentType } from "@remixicon/react";
import omit from "lodash/omit";
import { ComponentProps } from "react";
import { Link as RouterLink } from "react-router";

import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { SegmentSizeIndicator } from "@/company/components/SegmentSizeIndicator";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { SegmentUrl } from "@reflag/shared/urls";

type FlagDisplayProps = {
  segment: Pick<SegmentDTO, "id" | "name">;
  highlight?: string;
  iconProps?: BoxProps & ComponentProps<RemixiconComponentType>;
  iconSize?: number;
} & (
  | ({ link: true } & Omit<LinkProps, "size">)
  | ({ link?: false | undefined } & FlexProps)
);

export function SegmentDisplay({
  segment,
  highlight,
  iconProps,
  iconSize,
  ...containerProps
}: FlagDisplayProps) {
  const { env } = useEnvironment();

  const sharedProps = {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
  };

  const content = (
    <>
      <Box as="span" color="dimmed" fontSize="sm" {...iconProps}>
        <SegmentSizeIndicator segmentId={segment.id} size={iconSize} />
      </Box>

      <Box as="span" flexShrink="1" minW="0">
        {highlight
          ? highlightStringMatch(segment.name, highlight)
          : segment.name}
      </Box>
    </>
  );

  if (containerProps.link) {
    return (
      <Link
        _hover={{ textDecoration: "underline" }}
        as={RouterLink}
        textDecoration="none"
        {...sharedProps}
        {...omit(containerProps, "link")}
        to={SegmentUrl(env, segment)}
      >
        {content}
      </Link>
    );
  } else {
    return (
      <Flex as="span" {...sharedProps} {...containerProps}>
        {content}
      </Flex>
    );
  }
}
